// @flow
import { get } from 'lodash';
import moment from 'moment';
import URI from 'urijs';
import { constants, windowWrapper } from '@clubmed/utils';

import type { BadgeProps } from 'Components/frameworks/badge/Badge.jsx';
import { type IconName } from 'Components/frameworks/icon/Icon.jsx';
import type { PricingTableProps } from 'Components/frameworks/pricing-table/PricingTable.jsx';
import type { OfferDiscountProps } from 'Components/widgets/offer-discount/OfferDiscount.jsx';
import type { ProductProps } from 'Components/widgets/product/Product.utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import {
  getProductBadgeIcon,
  isCruise,
  isExclusiveCollection,
  isJoyview,
  isSummer,
  isTour,
  IS_DISABLED,
  IS_ENABLED,
  IS_SELECTED,
} from 'Components/utils/product/product.utils';
import { getAggregateDiscount } from 'Components/widgets/products-comparison/ProductsComparisonUtils';
import { t } from 'Src/i18n';
import { formatApiDate } from 'Utils/date/apiDate';
import type {
  ThumbnailProduct,
  ThumbnailProductPrice,
} from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import { QUERY_DEPARTURE_DATE, QUERY_DURATION, QUERY_TRANSPORT } from 'Utils/uri/constants';
import { generateUrlWithParams, getQueryParameters } from 'Utils/uri/uriUtils';

import type { ProductComparisonProps } from './product-comparison/ProductComparison.jsx';
import type { ProductFlagProps } from './product-flag/ProductFlag.jsx';
import type { ProductHeaderProps } from './product-header/ProductHeader.jsx';
import type { ProductThumbnailProps } from './product-thumbnail/ProductThumbnail.jsx';

type ProductCallToActionProps = {
  label?: string,
};

type ProductPriceProps = {
  ...$Exact<ThumbnailProductPrice>,
  style: ?{
    backgroundColor: string,
  },
  color: string,
  from?: string,
  instead?: string,
  flightIncluded?: string,
  viewRates?: string,
};

export type AvailabilityEnum = 'AVAILABLE' | 'UNAVAILABLE' | 'PARTIAL' | 'UNKNOWN';
export type ProductChildcareAvailability = {
  id: string,
  availability: AvailabilityEnum,
  ageMin: number,
  ageMax: number,
  remainingStock: number | null,
};

export type ProductChildcareAvailabilitiesProps = {
  items: Array<ProductChildcareAvailability>,
  monthsLabel: string,
  yearLabel: string,
  yearsLabel: string,
  remainingLabel: { n: string, _1: string },
  availableLabel: string,
  unavailableLabel: string,
  unknownLabel: string,
  childcareLabel: string,
};

type Props = {
  config: { [string]: * },
  comparison?: {
    isDisabled: boolean,
    isSelected: boolean,
  },
  displayValidationButton?: boolean,
  hasComparison?: boolean,
  isSliderDisabled?: boolean,
  offerColor?: string,
  contextualizeUrl?: boolean,
  onClick?: () => *,
  product: ThumbnailProduct,
  isPriceLoading?: boolean,
};

type ProductDiscountProps = {
  config: { [string]: * },
  product: ProductToCompare,
};

const { TOUR_TYPES, OPENING_STATUS } = constants;

const DEFAULT_COMPARISON = {
  isDisabled: false,
  isSelected: false,
};

function getBadgeIconProps(props: Props): BadgeProps {
  const { product } = props;
  return getProductBadgeIcon(product);
}

function getLogo(product: $Shape<ThumbnailProduct>) {
  return isJoyview(product.type)
    ? {
        alt: 'Joyview',
        color: COLORS.WATER_GREEN,
        name: ICONS.CLUBMED_JOYVIEW,
      }
    : {};
}

function getTitle(product: ThumbnailProduct): string {
  return product.name;
}

function getSubTitle(product: ThumbnailProduct): string {
  if (isCruise(product.type)) {
    return product.timingDescription;
  }

  return get(product, 'destination.country', '');
}

function getProductUrl(props: Props): string {
  if (!props.contextualizeUrl) return props.product.href;

  const departureDate = `${get(props.product, 'price.termsAndConditions.departureDate', '')}`;
  const url = new URI(props.product.href).setQuery({
    departure_city: get(props.product, 'price.termsAndConditions.departureCity.value', undefined),
    duration: get(props.product, 'price.termsAndConditions.duration', undefined),
    date:
      departureDate !== 'null' && departureDate
        ? formatApiDate(departureDate, 'YYYY-MM-DD')
        : undefined,
  });
  return url.toString();
}

function getBadge(product: $Shape<ThumbnailProduct>): string {
  return product.seasonTrad;
}

function getTagsAndBadgeColor(product: $Shape<ThumbnailProduct>): string {
  if (isTour(product.type)) {
    return product.rangeId === TOUR_TYPES.ESCAPADE ? COLORS.KIWI : COLORS.OCHER;
  }
  if (isCruise(product.type)) {
    return COLORS.DEEP_BLUE;
  }
  if (isSummer(product.seasonName)) {
    return COLORS.ORANGE;
  }

  return COLORS.BLUE;
}

function getProductHeaderProps(props: Props): ProductHeaderProps {
  const { product } = props;
  const badgeIcon = getBadgeIconProps(props);
  const badge = getBadge(product);
  const badgeColor = getTagsAndBadgeColor(product);
  const link = { url: getProductUrl(props) };
  const heading = {
    title: getTitle(product),
    subtitle: getSubTitle(product),
  };
  const isResortExclusiveCollection = isExclusiveCollection(product.type, product.comfort);

  return {
    badge,
    badgeColor,
    badgeIcon,
    heading,
    isExclusiveCollection: isResortExclusiveCollection,
    link,
  };
}

function getProductComparisonProps(props: Props): ProductComparisonProps {
  const { comparison = DEFAULT_COMPARISON } = props;
  const { selected, selectResort, unavailableThumbnail } = get(
    props,
    'config.faceting.compare',
    {},
  );

  const type = get(props, 'product.type');

  let status = IS_ENABLED;

  if (comparison.isDisabled) {
    status = IS_DISABLED;
  } else if (comparison.isSelected) {
    status = IS_SELECTED;
  }

  const label = comparison.isSelected ? selected : selectResort;

  return {
    status,
    label,
    isEligible: !isTour(type) && !isCruise(type),
    eligibilityLabel: unavailableThumbnail,
  };
}

function getProductCallToActionProps(props: Props): ProductCallToActionProps {
  const type = get(props, 'product.type');
  const { thumbnailCTA, thumbnailTourCTA } = get(props, 'config.common', {});

  return {
    label: isTour(type) ? thumbnailTourCTA : thumbnailCTA,
  };
}

function getProductPriceProps(props: Props): ProductPriceProps {
  const { config, product, offerColor } = props;
  const color = isJoyview(product.type) ? COLORS.WATER_GREEN : '';

  return {
    style: {
      color: offerColor,
    },
    color: offerColor || color,
    ...get(config, 'price.format', {}),
    ...(product.price || {}),
  };
}

function getProductChildcareAvailabilitiesProps(props: Props): ProductChildcareAvailabilitiesProps {
  const { config, product } = props;
  const {
    months: monthsLabel,
    years: yearsLabel,
    year: yearLabel,
    available: availableLabel,
    unknown: unknownLabel,
    unavailable: unavailableLabel,
    urgentizationSingular,
    urgentizationPlural,
    childcare: childcareLabel,
  } = get(config, 'faceting.availabilities') || {};

  let sortedChildcareAvailabilities = (product.childcareAvailabilities || []).sort(
    (a, b) => a.ageMin - b.ageMin,
  );
  const [first = {}, second = {}]: Array<
    ProductChildcareAvailability,
  > = sortedChildcareAvailabilities;
  const hasChildBetween4And23Months = first.ageMin === 4 || first.ageMax === 23;
  const hasOnlyBetween4And13Months = first.ageMin === 4 && second.ageMax !== 23;
  const hasOnlyBetween14And23Months = first.ageMax === 23;

  if (hasOnlyBetween4And13Months) first.ageMax = 23;
  else if (hasOnlyBetween14And23Months) first.ageMin = 4;
  else if (hasChildBetween4And23Months) {
    const stock =
      first.remainingStock === null && second.remainingStock === null
        ? null
        : (first.remainingStock || 0) + (second.remainingStock || 0);
    let availability = 'UNKNOWN';
    if (stock === 0) availability = 'UNAVAILABLE';
    else if (stock) availability = stock <= 10 ? 'PARTIAL' : 'AVAILABLE';
    sortedChildcareAvailabilities = [
      {
        ...second,
        remainingStock: stock,
        ageMin: 4,
        ageMax: 23,
        availability,
      },
      ...sortedChildcareAvailabilities.slice(2),
    ];
  }

  return {
    items: sortedChildcareAvailabilities,
    monthsLabel,
    yearsLabel,
    yearLabel,
    availableLabel,
    unavailableLabel,
    unknownLabel,
    remainingLabel: { n: urgentizationPlural, _1: urgentizationSingular },
    childcareLabel,
  };
}

function getPricingTableLabel(
  productType: string,
  labelJoyView: string,
  labelDefault: string,
): string {
  if (isTour(productType)) return '';
  return isJoyview(productType) ? labelJoyView : labelDefault;
}

function getPricingTableIcons(productType: string, hasTransport: boolean): Array<IconName> {
  if (isJoyview(productType) || isTour(productType)) return [];

  const icons = [ICONS.ACCOMODATIONS, ICONS.BAR, ICONS.DISHES];

  hasTransport ? icons.unshift(ICONS.TRANSPORT) : icons.unshift(ICONS.NO_TRANSPORT);

  return icons;
}

function getPricingTableInfos(props: Props): Array<*> {
  const { config, product } = props;

  const infos = [];

  // departureDate
  if (!isTour(product.type)) {
    const departureDate = moment(
      get(product, 'price.termsAndConditions.departureDate'),
      'YYYYMMDD',
    ).format(get(config, 'resort.startingPrice.dateFormat', 'D MMMM YYYY'));

    const departureDateLabel = get(config, 'resort.startingPrice.departureDateLabel');

    infos.push(`${departureDateLabel} ${departureDate}`);

    // durationFormatted
    infos.push(get(product, 'price.termsAndConditions.durationFormatted'));
  }

  // duration
  if (isTour(product.type)) {
    infos.push(get(product, 'duration'));
  }

  // departureCity
  const departureCityLabel = get(config, 'resort.startingPrice.departureCityLabel');
  const departureCity = get(product, 'price.termsAndConditions.departureCity.name');

  if (departureCity) {
    infos.push(`${departureCityLabel} ${departureCity}`);
  }

  // accommodation
  infos.push(get(product, 'price.termsAndConditions.accommodationComfortLabel'));

  return infos.filter(info => info);
}

function getProductPricingTableProps(props: Props): PricingTableProps {
  const { config, product, displayValidationButton = true } = props;

  const buttonLabel = isTour(product.type)
    ? get(config, 'resort.pricePopin.buttonTourLabel')
    : get(config, 'resort.pricePopin.buttonLabel');

  return {
    button: {
      children: displayValidationButton ? buttonLabel : '',
    },
    icons: getPricingTableIcons(
      product.type,
      !!get(product, 'price.termsAndConditions.departureCity.value'),
    ),
    infos: getPricingTableInfos(props),
    label: getPricingTableLabel(
      product.type,
      get(config, 'resort.startingPrice.roomOnly', ''),
      get(config, 'resort.startingPrice.allInclusive', ''),
    ),
    noPriceLabel: '',
    price: {
      bestPrice: get(product, 'price.bestPrice'),
      from: get(config, 'price.format.from'),
      hasInitialPrice: !!get(product, 'price.initialPrice.value'),
      initialPrice: get(product, 'price.initialPrice'),
      instead: get(config, 'price.format.instead'),
      unit: get(product, 'price.unit'),
      viewRates: get(config, 'price.format.viewRates'),
    },
  };
}

function getProductTags(product: $Shape<ThumbnailProduct>): ?string {
  if (isTour(product.type)) {
    const rangeLabel = product.rangeLabel.toUpperCase();
    return product.duration ? `${rangeLabel} : ${product.duration}` : rangeLabel;
  }
  if (isCruise(product.type)) {
    return `${product.thematics?.[0]?.label} - ${product.duration}`;
  }
}

function getProductThumbnailProps(props: Props): ProductThumbnailProps {
  const { product, isSliderDisabled = false } = props;
  const productTags = getProductTags(product);
  const tagsColor = getTagsAndBadgeColor(product);
  const link = { url: getProductUrl(props) };
  const { openingStatus } = product;
  const pictures = get(product, 'images', []).map(image => ({
    alt: product.name,
    src: image || '',
  }));
  const hasTransport = !!product?.price?.termsAndConditions?.departureCity?.value;

  const hasBorder = isExclusiveCollection(product.type, product.comfort);

  return {
    closedLabel: t('common.closed'),
    hasBorder,
    hasTransport,
    link,
    openingSoonLabel: t('common.openingSoon'),
    pictures,
    productTags,
    tagsColor,
    transportLabel: t('price.format.flightIncluded'),
    isSliderDisabled,
    openingStatus,
  };
}

function getProductFlagProps(props: Props): ProductFlagProps {
  const { isPriceLoading = false, product } = props;
  const { openingStatus = OPENING_STATUS.OPEN } = product;

  const price = openingStatus === OPENING_STATUS.OPEN ? getProductPriceProps(props) : {};

  return {
    price,
    pricingTable: getProductPricingTableProps(props),
    isPriceLoading,
  };
}

function getProductOfferDiscountProps(props: ProductDiscountProps): OfferDiscountProps {
  const { product, config } = props;

  return {
    discount: getAggregateDiscount(product),
    i18n: config.offerDiscount,
  };
}

function createProductUrlWithParams(href: string, productType: string) {
  const currentLocation = windowWrapper.getLocation();
  let possibleQueries = [QUERY_DEPARTURE_DATE, QUERY_DURATION, QUERY_TRANSPORT];
  if (isCruise(productType)) {
    possibleQueries = [QUERY_TRANSPORT];
  } else if (isTour(productType)) {
    possibleQueries = [QUERY_DEPARTURE_DATE, QUERY_TRANSPORT];
  }
  return generateUrlWithParams(href, {
    queryParams: possibleQueries.reduce(
      (acc, curr) => ({
        ...acc,
        [curr]: get(getQueryParameters(currentLocation), curr, ''),
      }),
      {},
    ),
  });
}

function setProductHref(props: Props): Props {
  const { product = {} } = props;
  const href = get(product, 'href');
  if (href && typeof window !== 'undefined') {
    return {
      ...props,
      // $FlowFixMe
      product: {
        // $FlowFixMe
        ...product,
        href: createProductUrlWithParams(href, product.type),
      },
    };
  }
  return props;
}

function getProductProps(props: Props): ProductProps {
  const { product = {}, hasComparison = false } = props;
  // $FlowFixMe
  return {
    comparison: getProductComparisonProps(props),
    callToAction: getProductCallToActionProps(props),
    flag: getProductFlagProps(props),
    hasComparison,
    header: getProductHeaderProps(props),
    id: product.id,
    thumbnail: getProductThumbnailProps(props),
    url: getProductUrl(props),
    type: product.type,
    childcare: getProductChildcareAvailabilitiesProps(props),
  };
}

function updateProduct(product: ProductProps, props: Props): ProductProps {
  return {
    ...product,
    hasComparison: props.hasComparison,
    childcare: getProductChildcareAvailabilitiesProps(props),
    comparison: getProductComparisonProps(props),
    flag: getProductFlagProps(props),
  };
}

function updateProductUrl(product: ProductProps): ProductProps {
  const newProductUrl = createProductUrlWithParams(new URI(product.url).pathname(), product.type);
  return {
    ...product,
    url: newProductUrl,
    header: {
      ...product.header,
      link: { url: newProductUrl },
    },
    // $FlowFixMe
    thumbnail: {
      ...product.thumbnail,
      link: { url: newProductUrl },
    },
  };
}

export {
  getBadge,
  getTagsAndBadgeColor,
  getLogo,
  getProductProps,
  getProductTags,
  getProductOfferDiscountProps,
  getProductCallToActionProps,
  getProductChildcareAvailabilitiesProps,
  getProductComparisonProps,
  getProductFlagProps,
  getProductHeaderProps,
  getProductPriceProps,
  getProductPricingTableProps,
  getProductThumbnailProps,
  setProductHref,
  updateProductUrl,
  createProductUrlWithParams,
  updateProduct,
};
